import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { _attachFilterGetter } from "../services/pgService";
import { getBsLastDate } from "../services/balance_ch";
import { AuthCtx } from "./AuthCtx";
import {
  DEFAULT_FISCAL_YEAR_START_MONTH,
  DEFAULT_PERIOD_MODE,
  normalizeDefaultPeriodMode,
  normalizeFiscalYearStartMonth,
  resolveDashboardDefaultRange,
} from "../components/dashboard/dashboardFiscalPeriod";
import { normalizeDashboardAmountUnit, resolveDashboardNumberFormat } from "../components/dashboard/dashboardAmountFormat";

const defaultRange = resolveDashboardDefaultRange();
const defaultFrom = defaultRange.from;
const defaultTo = defaultRange.to;
const STORAGE_KEY = "report360.dashboardFilters.v1";

const defaultFilterState = {
  connIds: [],
  compIds: [],
  from: defaultFrom,
  to: defaultTo,
  grain: "month",
  costCentre: "",
  fiscalYearStartMonth: DEFAULT_FISCAL_YEAR_START_MONTH,
  dashboardDefaultPeriodMode: DEFAULT_PERIOD_MODE,
  latestTransactionDate: "",
  dashboardNumberFormat: "indian",
  dashboardAmountUnit: "auto",
  dashboardDefaultsKey: "",
};

function featureSettingsFromTenant(tenantMeta) {
  const settings = tenantMeta?.feature_settings || {};
  return {
    fiscalYearStartMonth: normalizeFiscalYearStartMonth(settings.fiscal_year_start_month),
    dashboardDefaultPeriodMode: normalizeDefaultPeriodMode(settings.dashboard_default_period_mode),
    dashboardNumberFormat: resolveDashboardNumberFormat(settings.dashboard_number_format, tenantMeta),
  };
}

function readStoredFilterState() {
  if (typeof window === "undefined") return defaultFilterState;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultFilterState;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return defaultFilterState;
    const connIds = Array.isArray(parsed.connIds) ? parsed.connIds.map((id) => String(id)).filter(Boolean) : defaultFilterState.connIds;
    const compIds = Array.isArray(parsed.compIds) ? parsed.compIds.map((id) => String(id)).filter(Boolean) : defaultFilterState.compIds;
    const dashboardNumberFormat = parsed.dashboardNumberFormat === "international" ? "international" : "indian";
    return {
      ...defaultFilterState,
      connIds,
      compIds,
      from: typeof parsed.from === "string" && parsed.from ? parsed.from : defaultFilterState.from,
      to: typeof parsed.to === "string" && parsed.to ? parsed.to : defaultFilterState.to,
      grain: typeof parsed.grain === "string" && parsed.grain ? parsed.grain : defaultFilterState.grain,
      costCentre: typeof parsed.costCentre === "string" ? parsed.costCentre : defaultFilterState.costCentre,
      fiscalYearStartMonth: normalizeFiscalYearStartMonth(parsed.fiscalYearStartMonth),
      dashboardDefaultPeriodMode: normalizeDefaultPeriodMode(parsed.dashboardDefaultPeriodMode),
      latestTransactionDate: typeof parsed.latestTransactionDate === "string" ? parsed.latestTransactionDate : "",
      dashboardNumberFormat,
      dashboardAmountUnit: normalizeDashboardAmountUnit(parsed.dashboardAmountUnit || "auto", dashboardNumberFormat),
      dashboardDefaultsKey: typeof parsed.dashboardDefaultsKey === "string" ? parsed.dashboardDefaultsKey : "",
    };
  } catch {
    return defaultFilterState;
  }
}

function writeStoredFilterState(state) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        connIds: Array.isArray(state.connIds) ? state.connIds.map((id) => String(id)).filter(Boolean) : [],
        compIds: Array.isArray(state.compIds) ? state.compIds.map((id) => String(id)).filter(Boolean) : [],
        from: state.from,
        to: state.to,
        grain: state.grain,
        costCentre: state.costCentre,
        fiscalYearStartMonth: normalizeFiscalYearStartMonth(state.fiscalYearStartMonth),
        dashboardDefaultPeriodMode: normalizeDefaultPeriodMode(state.dashboardDefaultPeriodMode),
        latestTransactionDate: state.latestTransactionDate || "",
        dashboardNumberFormat: state.dashboardNumberFormat === "international" ? "international" : "indian",
        dashboardAmountUnit: normalizeDashboardAmountUnit(state.dashboardAmountUnit || "auto", state.dashboardNumberFormat),
        dashboardDefaultsKey: state.dashboardDefaultsKey || "",
      }),
    );
  } catch {
    // Ignore storage failures; dashboard filters still work for the current session.
  }
}

export const FilterCtx = createContext({
  connIds: [],
  compIds: [],
  from: defaultFrom,
  to: defaultTo,
  grain: "month",
  costCentre: "",
  fiscalYearStartMonth: DEFAULT_FISCAL_YEAR_START_MONTH,
  dashboardDefaultPeriodMode: DEFAULT_PERIOD_MODE,
  latestTransactionDate: "",
  dashboardNumberFormat: "indian",
  dashboardAmountUnit: "auto",
  setFilter: () => {},
});

export function FilterProvider({ children }) {
  const { tenantMeta, authReady } = useContext(AuthCtx);
  const [state, setState] = useState(() => readStoredFilterState());
  const tenantFiscalSettings = useMemo(() => featureSettingsFromTenant(tenantMeta), [tenantMeta]);
  const tenantDefaultsKey = `${tenantMeta?.tenant_id || "tenant"}:${tenantFiscalSettings.fiscalYearStartMonth}:${tenantFiscalSettings.dashboardDefaultPeriodMode}`;

  useEffect(() => {
    _attachFilterGetter(() => state);
  }, [state]);

  useEffect(() => {
    writeStoredFilterState(state);
  }, [
    state.from,
    state.to,
    state.grain,
    state.costCentre,
    state.connIds,
    state.compIds,
    state.fiscalYearStartMonth,
    state.dashboardDefaultPeriodMode,
    state.latestTransactionDate,
    state.dashboardNumberFormat,
    state.dashboardAmountUnit,
    state.dashboardDefaultsKey,
  ]);

  useEffect(() => {
    if (!authReady || !tenantMeta) return undefined;
    let cancelled = false;
    const { fiscalYearStartMonth, dashboardDefaultPeriodMode, dashboardNumberFormat } = tenantFiscalSettings;
    const shouldApplyDefaultRange = state.dashboardDefaultsKey !== tenantDefaultsKey || !state.from || !state.to;

    async function applyTenantDefaults() {
      let latestTransactionDate = "";
      if (dashboardDefaultPeriodMode === "till_latest_transaction") {
        try {
          const meta = await getBsLastDate({ compIds: state.compIds || [] });
          latestTransactionDate = String(meta?.last_voucher_date || meta?.last_date || "");
        } catch {
          latestTransactionDate = "";
        }
      }
      if (cancelled) return;
      const defaultRangeForTenant = resolveDashboardDefaultRange({
        fiscalYearStartMonth,
        defaultPeriodMode: dashboardDefaultPeriodMode,
        latestTransactionDate,
      });
      setState((current) => {
        const next = {
          ...current,
          fiscalYearStartMonth,
          dashboardDefaultPeriodMode,
          latestTransactionDate,
          dashboardNumberFormat,
          dashboardAmountUnit: normalizeDashboardAmountUnit(current.dashboardAmountUnit || "auto", dashboardNumberFormat),
          dashboardDefaultsKey: tenantDefaultsKey,
        };
        if (shouldApplyDefaultRange) {
          next.from = defaultRangeForTenant.from;
          next.to = defaultRangeForTenant.to;
        }
        return next;
      });
    }

    applyTenantDefaults();
    return () => {
      cancelled = true;
    };
  }, [
    authReady,
    tenantMeta,
    tenantDefaultsKey,
    tenantFiscalSettings,
    state.compIds,
    state.dashboardDefaultsKey,
    state.from,
    state.to,
  ]);

  return (
    <FilterCtx.Provider value={{ ...state, setFilter: setState }}>
      {children}
    </FilterCtx.Provider>
  );
}
